import _ from 'lodash';
import React, { ReactElement } from 'react';
import HomeIcon from '@material-ui/icons/Home';
import ShareIcon from '@material-ui/icons/Share';
import { getDistanceFromLatLonInKm } from '../../utils/mapUtils';
import { API_UPDATE_DRIVER_ACTIVATION, DRIVERS_RAYON, DRIVER_STATUS } from '../../constants';
import { getIdToken } from '../../apis/firebase';
import { updateCollection } from '../../apis/shop.service';
import { sendPostRequest } from '../../utils/apiService';
import { isFreelanceEnabled } from 'src/utils/delivery';
import { DishopDriver, DriverList, ExtendedDriverList, ExtendedDriverData } from 'src/models/drivers/driver';
import { DriversViewState } from './Drivers.view';
import { DRIVER_TABLE_HEAD } from 'src/constants/table';
import { initPage } from 'src/actions';

export type NavigationIcons = {
    icon?: ReactElement,
    label: string
}

export type DriversViewProps = {
    location: {
        latitude: number,
        longitude: number
    }
    shopId: string
}

export const getBottomNavigationIcon = (driverTypes: string[]) => {
    const newData: NavigationIcons[] = [];
    _.map(driverTypes, (driverType: string) => {
        newData.push({
            label: driverType,
            icon: driverType === 'Employés' ? <HomeIcon /> : <ShareIcon />
        });
    });
    return newData;
};

export const handleDriversData = (
    drivers: DriverList,
    independentDrivers: DriverList,
    selectedTab: number,
    setComponentState: React.Dispatch<React.SetStateAction<DriversViewState>>,
    props: DriversViewProps) => {

    const returnDrivers = initDrivers(drivers, false, props);
    const returnIndependentDrivers = initDrivers(independentDrivers, true, props);
    setComponentState(prevState => ({
        ...prevState,
        isLoading: false,
        currentDrivers: selectedTab > 0 ? returnIndependentDrivers : returnDrivers
    }));
};

const initDrivers = (driversList: DriverList, isIndependent: boolean, props: DriversViewProps) => {
    const returnDrivers: ExtendedDriverList = [];
    driversList &&
        _.map(driversList, (driver: DishopDriver) => {
            const driverShop = driver?.shopIds?.[props.shopId];
            let enabled = driverShop?.enabled || false
            if (isIndependent && !_.has(driverShop, 'enabled')) enabled = true;
            const driverData: ExtendedDriverData = {
                ...driver,
                id: driver?.driverId,
                nbOrderSuccess: driverShop?.completedOrders || 0,
                speed: 0,
                distance: 0,
                duration: 0,
                isDriverClose: getDistanceFromLatLonInKm(driver.lat, driver.long, props.location.latitude, props.location.longitude) < DRIVERS_RAYON,
                enabled
            };
            driverData.onTransaction = setDriverDataOnTransactionValue(driverData);
            const isDriverAvailable = checkDriverAvailability(driverData, driverShop, isIndependent);
            if (isDriverAvailable) {
                returnDrivers.push(driverData);
            }
        });
    return returnDrivers;
};

const checkDriverAvailability = (driver: ExtendedDriverData, driverShop: any, isIndependent: boolean) => {
    if (driver?.email) {
        const isIndependentDriverClose = driver?.isDriverClose && isIndependent;
        const isShopDriverEligible = !isIndependent && driverShop;
        const isIndependentWithHistory = isIndependent && driver?.completedOrders > 0
        const isNonShopDriverEligible =
            !isIndependent &&
            !driverShop &&
            (driver?.isDriverClose || driver?.completedOrders > 0);


        return isIndependentDriverClose || isIndependentWithHistory || isShopDriverEligible || isNonShopDriverEligible;
    }
    return false;
}

const setDriverDataOnTransactionValue = (driverData: ExtendedDriverData) => {
    if (driverData?.status === 'delivering') {
        return DRIVER_STATUS.delivering;
    }
    if (driverData?.status && driverData.isDriverClose) {
        return DRIVER_STATUS.available;
    }
    if (!driverData?.tokenNotifications) {
        if (driverData?.status === 'uninstall') {
            return DRIVER_STATUS.uninstalled;
        }
        else if (driverData?.freelance?.verification) {
            return DRIVER_STATUS.noNotifications
        }
        return DRIVER_STATUS.documentsRequired
    }
    if (!driverData?.freelance?.verification && driverData?.isFreelance) {
        return DRIVER_STATUS.documentsRequired;
    }
    else {
        return DRIVER_STATUS.unavailable;
    }
}

export const showDriversTypeButton = (setComponentState: React.Dispatch<React.SetStateAction<DriversViewState>>, shopId: string) => {
    const newListButton = ['Employés'];
    if (isFreelanceEnabled(shopId)) {
        newListButton.push('Indépendants');
    }
    setComponentState(prevState => ({
        ...prevState,
        listButton: newListButton,
        selectedTab: 0
    }));
};

export const onChangeData = (
    e: React.ChangeEvent<HTMLInputElement>,
    value: number,
    independentDrivers: ExtendedDriverList,
    drivers: ExtendedDriverList,
    setComponentState: React.Dispatch<React.SetStateAction<DriversViewState>>,
    dispatch,
    props: DriversViewProps
) => {
    const returnDrivers = initDrivers(drivers, false, props);
    const returnIndependentDrivers = initDrivers(independentDrivers, true, props);
    dispatch(initPage());
    e.preventDefault();
    setComponentState(prevState => ({
        ...prevState,
        selectedTab: value,
        currentDrivers: value === 1 ? returnIndependentDrivers : returnDrivers
    }))
};

export const defineDriversTableHeads = (isIndependentTab: boolean, canToggleDrivers: boolean) => {
    if (!isIndependentTab && canToggleDrivers) {
        return DRIVER_TABLE_HEAD;
    }
    const idsToRemove: string[] = [];
    if (!canToggleDrivers) {
        idsToRemove.push('enabled');
    }
    if (isIndependentTab) {
        idsToRemove.push('cash');
    }
    return DRIVER_TABLE_HEAD.filter(item => !idsToRemove.includes(item.id));
}

export const switchDriverActivation = async (
    driverId: string,
    shopId: string,
    freelance: boolean,
    enabled: boolean,
    currentDrivers: ExtendedDriverList,
    setComponentState: React.Dispatch<React.SetStateAction<DriversViewState>>
) => {

    const idToken = await getIdToken();
    await sendPostRequest(
        API_UPDATE_DRIVER_ACTIVATION,
        {
            shopId,
            driverId,
            freelance,
            enabled
        },
        { Authorization: `Bearer ${idToken}` }
    );
    const updatedDrivers = currentDrivers.map(currentDriver =>
        currentDriver.id === driverId ? { ...currentDriver, enabled } : currentDriver
    );
    setComponentState(prevState => ({ ...prevState, currentDrivers: updatedDrivers }))
};

export const handleSubmit = async (
    driver: ExtendedDriverData,
    currentDrivers: ExtendedDriverList,
    setComponentState: React.Dispatch<React.SetStateAction<DriversViewState>>) => {

    const { id, cash = 0, cb = 0, ticket_resto = 0 } = driver;
    await updateCollection(id, cash, cb, ticket_resto);
    const updatedDrivers = currentDrivers.map(currentDriver =>
        currentDriver.id === id ? { ...currentDriver, cash, cb, ticket_resto } : currentDriver
    );

    setComponentState(prevState => ({
        ...prevState,
        modalOpen: false,
        showModalUpdateDriver: false,
        currentDrivers: updatedDrivers,
    }));

};

export const handleCloseModal = (setComponentState: React.Dispatch<React.SetStateAction<DriversViewState>>) => {
    setComponentState(prevState => ({ ...prevState, modalOpen: false }));
};

export const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setSelectedDriver: React.Dispatch<React.SetStateAction<ExtendedDriverData | null>>
) => {
    const targetId = e.target.id;
    const value = e.target.value === '' ? 0 : Number(e.target.value);
    const numericValue = value < 0 ? 0 : value;

    setSelectedDriver(prevState => {
        if (!prevState) return null;
        return { ...prevState, [targetId]: numericValue };
    });
};
